/**
 * Trades Screen - History Style
 * Matches the design from History screen
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  FlatList,
  RefreshControl,
  ActivityIndicator,
  Modal,
  Animated,
  Image,
  Dimensions,
} from 'react-native';
import Icon from '../components/Icon';
import { useTheme } from '../context/ThemeContext';
import { authAPI, userTradesAPI, userSummaryAPI } from '../services/api';
import { vs, hs, ms, iconSize, fontSize, spacing } from '../utils/responsive';
import { formatTradeDateTime } from '../utils/dateFormatter';

const { width: WINDOW_WIDTH } = Dimensions.get('window');

const TradesScreen = ({ navigation }) => {
  // All hooks must be called at the top level - never inside conditionals
  const { colors, isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('POSITIONS');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [userId, setUserId] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [selectedServer, setSelectedServer] = useState('fizmoFXMarket-Server');
  const slideAnim = useRef(new Animated.Value(-WINDOW_WIDTH * 0.75)).current;

  // Fetch user profile for drawer
  const fetchUserProfile = async () => {
    try {
      // Check if user is logged in first
      const token = await authAPI.getToken();
      if (!token) {
        console.error('No auth token found for profile fetch');
        setIsLoadingProfile(false);
        return;
      }

      const profile = await authAPI.getProfile();
      setUserProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      
      // If it's an authentication error, handle gracefully
      if (error.response?.status === 401 || error.response?.status === 404) {
        console.warn('User authentication issue in profile fetch');
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchSelectedServer = async () => {
    try {
      // You can implement server selection logic here if needed
      setSelectedServer('fizmoFXMarket-Server');
    } catch (error) {
      console.error('Error fetching selected server:', error);
    }
  };

  const toggleDrawer = () => {
    if (drawerVisible) {
      Animated.timing(slideAnim, {
        toValue: -WINDOW_WIDTH * 0.75,
        duration: 300,
        useNativeDriver: true,
      }).start(() => {
        setDrawerVisible(false);
      });
    } else {
      setDrawerVisible(true);
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  // Fetch transactions using the same API and logic as HistoryScreen
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch User Summary API for wallet balance
      console.log('🔗 TradesScreen - Fetching User Summary API...');
      const summaryResponse = await userSummaryAPI.getUserSummary().catch(err => {
        console.log('TradesScreen - User Summary API error:', err);
        return null;
      });
      
      if (summaryResponse?.success) {
        const data = summaryResponse.data;
        
        // Use the same balance logic as HistoryScreen: data.actualBalance
        setWalletBalance(data.actualBalance || 0);
        console.log('TradesScreen - Balance (same as HistoryScreen):', data.actualBalance);
      } else {
        setWalletBalance(0);
      }
      
      // Fetch User Trades API
      console.log('🔗 TradesScreen - Fetching User Trades API...');
      const tradesResponse = await userTradesAPI.getUserTrades().catch(err => {
        console.log('TradesScreen - User Trades API error:', err);
        return null;
      });
      
      if (tradesResponse?.success && Array.isArray(tradesResponse.data)) {
        console.log('📊 TradesScreen - User Trades Response:', JSON.stringify(tradesResponse, null, 2));
        
        const transactionsArray = tradesResponse.data;
        const formattedData = [];
        
        // Process each transaction (same logic as HistoryScreen)
        transactionsArray.forEach((transaction, index) => {
          try {
            // Process all transactions, including N/A ones
            const buyPrice = parseFloat(transaction.buyPrice) || 0;
            const sellPrice = parseFloat(transaction.sellPrice) || 0;
            const quantity = parseFloat(transaction.quantity) || 0;
            const userReceived = parseFloat(transaction.userReceived) || 0;
            const commissionAmount = parseFloat(transaction.commission) || 0;
            
            // Calculate actual profit/loss from price difference
            let calculatedProfit = 0;
            if (buyPrice > 0 && sellPrice > 0 && quantity > 0) {
              // For sell trades: (buyPrice - sellPrice) * quantity
              if (transaction.tradeType === 'SELL') {
                calculatedProfit = (buyPrice - sellPrice) * quantity;
              }
              // For buy trades: (sellPrice - buyPrice) * quantity (same as sell)
              else if (transaction.tradeType === 'BUY') {
                calculatedProfit = (sellPrice - buyPrice) * quantity;
              }
            } else {
              // For commission entries or trades without price data, use userReceived
              calculatedProfit = userReceived;
            }
            
            // Determine display name
            let displaySymbol = 'Commission';
            if (transaction.tradeName && transaction.tradeName !== 'N/A') {
              displaySymbol = transaction.tradeName;
            }
            
            // Determine trade type
            let tradeType = 'buy';
            if (transaction.tradeType === 'SELL') {
              tradeType = 'sell';
            } else if (transaction.tradeType === 'BUY') {
              tradeType = 'buy';
            }
            
            formattedData.push({
              id: transaction.id || `trade_${index}_${Date.now()}`,
              symbol: displaySymbol,
              type: tradeType,
              volume: transaction.quantity?.toString() || '0',
              openPrice: (buyPrice / 100).toFixed(2),
              closePrice: (sellPrice / 100).toFixed(2),
              timestamp: formatTradeDateTime(transaction.date),
              profit: calculatedProfit.toFixed(2),
              totalProfit: calculatedProfit.toFixed(2), // Use calculated profit for color display
              commissionAmount: commissionAmount.toFixed(2),
              commission: commissionAmount, // For Balance Card
              showCommissionCard: false, // Don't show commission card in TradesScreen
              rawTransaction: transaction // Keep original for debugging
            });
          } catch (err) {
            console.error('TradesScreen - Error formatting transaction:', err, transaction);
          }
        });
        
        console.log('📊 TradesScreen - Formatted Transactions:', formattedData.length);
        
        // Filter to show only today's trades
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const filteredTrades = formattedData.filter((trade) => {
          try {
            if (!trade.rawTransaction?.date) return false;
            
            const tradeDate = new Date(trade.rawTransaction.date);
            tradeDate.setHours(0, 0, 0, 0);
            
            // Show only today's trades
            return tradeDate.getTime() === today.getTime();
          } catch (err) {
            return false;
          }
        });
        
        console.log('📊 TradesScreen - Today\'s Trades:', filteredTrades.length);
        setTransactions(filteredTrades);
      } else {
        console.log('❌ TradesScreen - User Trades API failed or no data');
        setTransactions([]);
      }
      
    } catch (error) {
      console.error('TradesScreen - Error in fetchTransactions:', error);
      setTransactions([]);
      setWalletBalance(0);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchTransactions();
  };

  useEffect(() => {
    fetchUserProfile();
    fetchSelectedServer();
    fetchTransactions();
  }, []);

  const menuItems = [
    { id: '1', icon: 'trending-up-outline', label: 'Trade', screen: 'Trades' },
    { id: '2', icon: 'newspaper-outline', label: 'News', screen: null },
    { id: '3', icon: 'mail-outline', label: 'Mailbox', screen: null },
    { id: '4', icon: 'calendar-outline', label: 'Journal', screen: null },
    { id: '5', icon: 'settings-outline', label: 'Settings', screen: 'Profile' },
    { id: '6', icon: 'calendar-outline', label: 'Economic calendar', badge: 'Ads', screen: null },
    { id: '7', icon: 'people-outline', label: 'Traders Community', screen: null },
    { id: '8', icon: 'code-slash-outline', label: 'MQL5 Algo Trading', screen: null },
    { id: '9', icon: 'help-circle-outline', label: 'User guide', screen: null },
    { id: '10', icon: 'information-circle-outline', label: 'About', screen: null },
  ];

  const renderTradeItem = ({ item }) => {
    return (
      <View>
        {/* Trade Card */}
        <View style={styles.tradeCard}>
          {/* Trade Header */}
          <View style={styles.tradeHeader}>
            <View style={styles.tradeHeaderLeft}>
              <Text style={[styles.symbolText, { color: colors.text }]}>{item.symbol}, </Text>
              <Text style={[styles.typeText, item.type === 'buy' ? styles.buyType : styles.sellType]}>
                {item.type} 
              </Text>
              <Text style={[styles.volumeText, item.type === 'sell' && styles.sellType]}> {parseFloat(item.volume || 0) % 1 === 0 ? Math.round(parseFloat(item.volume || 0)) : parseFloat(item.volume || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.tradeHeaderRight}>
              <Text style={styles.timestampText}>{item.timestamp}</Text>
              <Text style={[
                styles.profitText, 
                { color: parseFloat(item.totalProfit) > 0 ? '#4A90E2' : parseFloat(item.totalProfit) < 0 ? '#EF4444' : colors.text }
              ]}>
                {parseFloat(item.totalProfit) > 0 ? '+' : parseFloat(item.totalProfit) < 0 ? '-' : ''}{Math.abs(parseFloat(item.totalProfit || 0)).toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Price Info */}
          <View style={styles.priceRow}>
            <Text style={styles.priceText}>
              {parseFloat(item.openPrice || 0).toFixed(2)}→{parseFloat(item.closePrice || 0).toFixed(2)}
            </Text>
          </View>
        </View>
        <View style={[styles.cardSeparatorLine, { backgroundColor: colors.border }]} />
        
        {/* Balance Card showing Admin Commission - Only show for commission entries */}
        {item.showCommissionCard && (
          <>
            <View style={styles.balanceCard}>
              <View style={styles.balanceRow}>
                <Text style={[styles.balanceLabel, { color: colors.text }]}>Balance</Text>
                <Text style={styles.balanceTimestamp}>{item.timestamp}</Text>
              </View>
              <View style={styles.balanceDetailRow}>
                <Text style={styles.balanceDetail}>Copy Commission Withdraw</Text>
                <Text style={styles.balanceProfit}>
                  -{parseFloat(item.commissionAmount || item.commission || 0).toFixed(2)}
                </Text>
              </View>
            </View>
            <View style={[styles.cardSeparatorLine, { backgroundColor: colors.border }]} />
          </>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Header Section */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <TouchableOpacity style={styles.menuButton} onPress={toggleDrawer}>
            <Icon name="menu" size={24} color={colors.primary} />
          </TouchableOpacity>
          <View>
            <Text style={[styles.headerTitle, { color: colors.primary }]}>Trades</Text>
          </View>
        </View>

        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerIcon}>
            <Icon name="swap-vertical" size={24} color={colors.primary} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerIcon}>
            <Icon name="arrow-down" size={24} color={colors.primary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Tabs */}
      <View style={[styles.tabsWrap, { borderBottomColor: colors.border }]}>
        {['POSITIONS', 'ORDERS', 'DEALS'].map((tab) => (
          <TouchableOpacity
            key={tab}
            style={[styles.tab, activeTab === tab && { borderBottomColor: colors.text }]}
            onPress={() => setActiveTab(tab)}
          >
            <Text
              style={[
                styles.tabText,
                { color: colors.textTertiary },
                activeTab === tab && { color: colors.text },
              ]}
            >
              {tab}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Summary Section - Always show balance information */}
      <View style={[styles.summarySection, { backgroundColor: colors.background }]}>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Balance:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { 
            color: walletBalance > 0 ? colors.text : colors.textSecondary 
          }]}>
            {walletBalance > 0 ? walletBalance.toFixed(2) : '0.00'}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Equity:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { 
            color: walletBalance > 0 ? colors.text : colors.textSecondary 
          }]}>
            {walletBalance > 0 ? walletBalance.toFixed(2) : '0.00'}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { color: colors.text }]}>Margin:</Text>
          <View style={styles.dotsContainer}>
            <Text style={[styles.summaryDots, { color: colors.border }]} numberOfLines={1}>••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••</Text>
          </View>
          <Text style={[styles.summaryValue, { 
            color: walletBalance > 0 ? colors.text : colors.textSecondary 
          }]}>
            {walletBalance > 0 ? walletBalance.toFixed(2) : '0.00'}
          </Text>
        </View>
      </View>

      {/* Separator Line */}
      <View style={[styles.separatorLine, { backgroundColor: colors.border }]} />

      {/* List */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#4A90E2" />
        </View>
      ) : (
        <FlatList
          data={walletBalance > 0 ? transactions : []}
          renderItem={renderTradeItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={{ paddingBottom: 1 }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={['#4A90E2']}
              tintColor="#4A90E2"
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>
                {walletBalance > 0 
                  ? "No trades found. Trade history will appear here when available."
                  : "Add balance to your wallet to start trading and view trades."
                }
              </Text>
            </View>
          }
        />
      )}

      {/* DRAWER MENU */}
      <Modal
        visible={drawerVisible}
        transparent
        animationType="none"
        onRequestClose={toggleDrawer}
      >
        <TouchableOpacity 
          style={styles.drawerOverlay} 
          activeOpacity={1} 
          onPress={toggleDrawer}
        >
          <Animated.View 
            style={[
              styles.drawerContainer,
              { transform: [{ translateX: slideAnim }], backgroundColor: colors.background, borderRightColor: colors.border }
            ]}
            onStartShouldSetResponder={() => true}
          >
            {/* Drawer Header */}
            <View style={[styles.drawerHeader, { borderBottomColor: colors.border }]}>
              <View style={styles.drawerProfile}>
                <View style={[styles.drawerAvatar, { backgroundColor: `${colors.primary}33`, borderColor: colors.primary }]}>
                  {userProfile?.image ? (
                    <Image 
                      source={{ uri: `https://a77.onetradebot.com/${userProfile.image}` }} 
                      style={styles.drawerAvatarImage}
                    />
                  ) : (
                    <Icon name="person" size={iconSize.lg} color={colors.primary} />
                  )}
                </View>
                <View style={styles.drawerInfo}>
                  {isLoadingProfile ? (
                    <ActivityIndicator size="small" color={colors.primary} />
                  ) : (
                    <>
                      <Text style={[styles.drawerName, { color: colors.text }]}>
                        {userProfile?.name || 'User'}
                      </Text>
                      <Text style={[styles.drawerServer, { color: colors.textTertiary }]}>
                        {userProfile?.userId || '1000077'} - {selectedServer}
                      </Text>
                      <TouchableOpacity 
                        onPress={() => {
                          toggleDrawer();
                          navigation.navigate('EditProfile');
                        }}
                        style={styles.manageAccountsContainer}
                      >
                        <Text style={[styles.manageAccountsText, { color: colors.primary }]}>
                          Manage accounts
                        </Text>
                      </TouchableOpacity>
                    </>
                  )}
                </View>
              </View>
            </View>

            {/* Menu Items */}
            <View style={styles.menuSection}>
              {menuItems.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={styles.menuItem}
                  onPress={() => {
                    toggleDrawer();
                    if (item.screen) {
                      navigation.navigate(item.screen);
                    }
                  }}
                >
                  <Icon name={item.icon} size={iconSize.md} color={colors.text} />
                  <View style={styles.labelContainer}>
                    <Text style={[styles.menuLabel, { color: colors.text }]}>{item.label}</Text>
                    {item.badge === 'Ads' && (
                      <View style={styles.adsContainer}>
                        <Text style={styles.adsText}>{item.badge}</Text>
                      </View>
                    )}
                  </View>
                  {item.badge && item.badge !== 'Ads' && (
                    <View style={[styles.badge, { backgroundColor: colors.error }]}>
                      <Text style={styles.badgeText}>{item.badge}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </Animated.View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0B0F2F',
  },

  header: {
    paddingTop: 12,
    paddingBottom: 15,
    paddingHorizontal: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },

  menuButton: {
    padding: 4,
  },

  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#4A90E2',
  },

  headerSubtitle: {
    fontSize: 21,
    color: '#4A90E2',
    marginTop: 2,
  },

  headerActions: {
    flexDirection: 'row',
    gap: 12,
  },

  headerIcon: {
    padding: 4,
  },

  tabsWrap: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(74, 144, 226, 0.3)',
  },

  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
  },

  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#FFFFFF',
  },

  tabText: {
    color: '#6B7280',
    fontSize: 13,
    fontWeight: '500',
    letterSpacing: 0.5,
  },

  activeTabText: {
    color: '#FFFFFF',
  },

  summarySection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#0B0F2F',
  },

  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },

  summaryLabel: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },

  dotsContainer: {
    flex: 1,
    marginHorizontal: 4,
    overflow: 'hidden',
  },

  summaryDots: {
    color: '#d6dbe2ff',
    fontSize: 8,
    letterSpacing: 1,
  },

  summaryValue: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
    textAlign: 'right',
  },

  separatorLine: {
    height: 1,
    backgroundColor: '#d6dbe2ff',
    marginHorizontal: -5,
  },

  cardSeparatorLine: {
    height: 1,
    backgroundColor: '#d6dbe2ff',
    marginHorizontal: -5,
  },

  tradeCard: {
    paddingHorizontal: 16,
    paddingVertical: 1,
    
  },

  tradeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 3,
  },

  tradeHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },

  symbolText: {
    fontSize: 15,
    fontWeight: '400',
  },

  typeText: {
    fontSize: 15,
    fontWeight: '600',
  },

  buyType: {
    color: '#4A90E2',
  },

  sellType: {
    color: '#EF4444',
  },

  volumeText: {
    color: '#4A90E2',
    fontSize: 15,
    fontWeight: '400',
  },

  tradeHeaderRight: {
    alignItems: 'flex-end',
  },

  timestampText: {
    color: '#9CA3AF',
    fontSize: 10,
    marginBottom: 2,
    fontWeight: '400',
  },

  profitText: {
    color: '#4A90E2',
    fontSize: 15,
    fontWeight: '400',
  },

  priceRow: {
    marginTop: -20,
    marginBottom: 4,
  },

  priceText: {
    color: '#9CA3AF',
    fontSize: 13,
    fontWeight: '400',
    textAlignVertical: 'center',
    lineHeight: 18,
  },

  balanceCard: {
    paddingHorizontal: 16,
    paddingVertical: 2,
  },

  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: -3,
  },

  balanceLabel: {
    fontSize: 15,
    fontWeight: '400',
  },

  balanceTimestamp: {
    color: '#d6dbe2ff',
    fontSize: 10,
    fontWeight: '400',
  },

  balanceDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  balanceDetail: {
    color: '#808080',
    fontSize: 11,
    fontWeight: '400',
    lineHeight: 14,
  },

  balanceProfit: {
    color: '#EF4444',
    fontSize: 13,
    fontWeight: '400',
  },

  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },

  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },

  emptyText: {
    color: '#9CA3AF',
    fontSize: 16,
  },

  // Drawer Styles
  drawerOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },

  drawerContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: WINDOW_WIDTH * 0.75,
    backgroundColor: '#000000',
    borderRightWidth: 1,
    borderRightColor: '#1F2937',
  },

  drawerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 16,
    paddingTop: 50,
    borderBottomWidth: 1,
    borderBottomColor: '#1F2937',
  },

  drawerProfile: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },

  drawerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(74, 144, 226, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#4A90E2',
    overflow: 'hidden',
  },

  drawerAvatarImage: {
    width: '100%',
    height: '100%',
  },

  drawerInfo: {
    flex: 1,
  },

  drawerName: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },

  drawerServer: {
    color: '#9CA3AF',
    fontSize: 12,
    marginBottom: 2,
  },

  manageAccountsContainer: {
    marginTop: 2,
    paddingTop: 2,
  },

  manageAccountsText: {
    fontSize: 14,
    fontWeight: '500',
  },

  menuSection: {
    paddingTop: 8,
    flex: 1,
  },

  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 16,
    gap: 16,
  },

  labelContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  menuLabel: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '400',
  },

  badge: {
    backgroundColor: '#EF4444',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    minWidth: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },

  badgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '600',
  },

  adsContainer: {
    borderWidth: 1,
    borderColor: '#4A90E2',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: 'transparent',
  },

  adsText: {
    color: '#4A90E2',
    fontSize: 12,
    fontWeight: '500',
  },
});

export default TradesScreen;

